/**
 * FieldRenderer Component
 * Universal field renderer supporting all field types
 * Replaces 1,141 duplicate lines across 7 detail pages
 */

import React, { useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import Select from 'react-select';
import 'react-quill/dist/quill.snow.css';
import type { FieldConfig, FieldValue, Person, Asset, Department } from '../types/models';
import { reactSelectStyles } from '../utils/reactSelectStyles';

declare const flatpickr: any;

interface FieldRendererProps {
  config: FieldConfig;
  value: FieldValue;
  onChange: (fieldId: string, value: FieldValue) => void;
  people?: Person[];
  assets?: Asset[];
  risks?: any[];
  departments?: Department[];
  /** Validation-error message to show below the field (e.g. "This field is required.").
   * FieldRenderer just presents whatever text it's given - the caller decides when a
   * field counts as invalid and clears this once the user fixes it. */
  error?: string;
}

// Semantic colour hints for the 'segmented' pill-toggle field type - keyed by
// lowercased option text so any field using known status-ish words gets
// coloured pills for free; anything unrecognised just falls back to plain
// primary/outline-secondary toggle styling.
export const SEGMENTED_SEMANTIC_COLOR: Record<string, string> = {
  'on track': 'success', done: 'success', active: 'success', yes: 'success', accepted: 'success',
  'due soon': 'warning', pending: 'warning', 'in review': 'warning', some: 'warning',
  overdue: 'danger', missed: 'danger', rejected: 'danger', no: 'danger', significant: 'danger',
  'not scheduled': 'secondary', 'not applicable': 'secondary', none: 'secondary',
};

const SEGMENTED_TONE_ALIAS: Record<string, string> = { secondary: 'neutral' };

export const segmentedPillTone = (option: string): string => {
  const color = SEGMENTED_SEMANTIC_COLOR[option.toLowerCase()];
  const tone = color ? (SEGMENTED_TONE_ALIAS[color] || color) : 'primary';
  return tone;
};

// Quill toolbar configuration
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['link'],
    ['clean']
  ]
};

// --- Flatpickr date input sub-component ---
interface FlatpickrDateFieldProps {
  inputId: string;
  label: string;
  required: boolean;
  value: string;
  readOnly: boolean;
  onChange: (v: string) => void;
  /** Skip the built-in label/wrapper margin - for embedding inside a compact
   * custom row layout that already renders its own label. */
  hideLabel?: boolean;
  error?: string;
}

export const FlatpickrDateField: React.FC<FlatpickrDateFieldProps> = ({
  inputId, label, required, value, readOnly, onChange, hideLabel = false, error,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const fpRef = useRef<any>(null);

  useEffect(() => {
    if (!ref.current || typeof flatpickr === 'undefined') return;
    if (fpRef.current) { fpRef.current.destroy(); fpRef.current = null; }
    if (readOnly) return;

    fpRef.current = flatpickr(ref.current, {
      dateFormat: 'Y-m-d',
      defaultDate: value || undefined,
      allowInput: true,
      onChange: (_selectedDates: Date[], dateStr: string) => onChange(dateStr),
    });

    return () => { if (fpRef.current) { fpRef.current.destroy(); fpRef.current = null; } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  // Sync value into flatpickr when it changes externally
  useEffect(() => {
    if (fpRef.current && value !== undefined) {
      fpRef.current.setDate(value, false);
    }
  }, [value]);

  return (
    <div className={hideLabel ? '' : 'np-field'}>
      {!hideLabel && (
        <label htmlFor={inputId}>
          {label}
          {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
        </label>
      )}
      <div className="input-group input-group-merge">
        <input
          ref={ref}
          type="text"
          id={inputId}
          className={`np-input${error ? ' is-invalid' : ''}`}
          defaultValue={value}
          readOnly={readOnly}
          required={required}
          placeholder="YYYY-MM-DD"
        />
        {!readOnly && (
          <span className="input-group-text cursor-pointer">
            <i className="ri-calendar-line"></i>
          </span>
        )}
      </div>
      {error && <div className="np-field-error"><i className="ri-error-warning-line"></i>{error}</div>}
    </div>
  );
};
// ------------------------------------------

export const FieldRenderer: React.FC<FieldRendererProps> = ({
  config,
  value,
  onChange,
  people = [],
  assets = [],
  risks = [],
  departments = [],
  error,
}) => {
  const {
    fieldId,
    fieldName,
    fieldType,
    isRequired,
    isEditable,
    placeholder,
    options = [],
  } = config;

  // Defensive sanitizers: never let option objects be rendered into JSX.
  const unwrapOptionObject = (val: any): any => {
    if (!val || typeof val !== 'object') return val;
    // Don't unwrap if this looks like a legitimate dropdown option definition
    // (used for procedure dropdowns, etc.)
    const keys = Object.keys(val);
    if (keys.length === 2 && 'value' in val && 'label' in val && typeof val.value === 'string' && typeof val.label === 'string') {
      return val; // Keep the option object intact
    }
    // Common corruption: react-select option objects stored in DynamoDB.
    if ('value' in val && 'label' in val) return (val as any).value;
    // Also unwrap objects that only carry a value.
    if ('value' in val && Object.keys(val).length === 1) return (val as any).value;
    return val;
  };

  const sanitizeValueDeep = (val: any): any => {
    const unwrapped = unwrapOptionObject(val);
    if (Array.isArray(unwrapped)) return unwrapped.map(sanitizeValueDeep);
    return unwrapped;
  };

  const toDisplayText = (val: any): string => {
    const sanitized = sanitizeValueDeep(val);
    if (sanitized === null || sanitized === undefined) return '';
    if (typeof sanitized === 'string') return sanitized;
    if (typeof sanitized === 'number' || typeof sanitized === 'boolean') return String(sanitized);
    try {
      return JSON.stringify(sanitized);
    } catch {
      return String(sanitized);
    }
  };

  const sanitizedValue = sanitizeValueDeep(value);
  const safeFieldName = toDisplayText(fieldName);
  const safePlaceholder = placeholder ? toDisplayText(placeholder) : undefined;
  const safeFieldTypeText = toDisplayText(fieldType);

  const safeSelectOptions: string[] = Array.isArray(options)
    ? (options as any[])
        .map((opt) => sanitizeValueDeep(opt))
        .map((opt) => (opt === null || opt === undefined ? '' : String(opt)))
        .filter((opt) => opt !== '')
    : [];

  const safeValueLabelOptions: Array<{ value: string; label: string }> = Array.isArray(options)
    ? (options as any[])
        .map((opt) => sanitizeValueDeep(opt))
        .map((opt) => {
          if (!opt) return null;
          if (typeof opt === 'string' || typeof opt === 'number' || typeof opt === 'boolean') {
            const str = String(opt);
            return { value: str, label: str };
          }
          if (typeof opt === 'object' && 'value' in opt) {
            const v = (opt as any).value;
            const l = (opt as any).label;
            const valueStr = v === null || v === undefined ? '' : String(v);
            const labelStr = l === null || l === undefined ? valueStr : String(l);
            if (!valueStr) return null;
            return { value: valueStr, label: labelStr };
          }
          return null;
        })
        .filter(Boolean) as Array<{ value: string; label: string }>
    : [];

  const handleChange = (newValue: FieldValue) => {
    onChange(fieldId, newValue);
  };

  // Common props
  const inputId = `field_${fieldId}`;
  const isReadonly = !isEditable;
  const required = isRequired;
  const invalidClass = error ? ' is-invalid' : '';
  const errorMessage = error ? (
    <div className="np-field-error"><i className="ri-error-warning-line"></i>{error}</div>
  ) : null;

  // Readonly field (always read-only, regardless of isEditable)
  if (fieldType === 'readonly') {
    return (
      <div className="np-field">
        <label htmlFor={inputId}>
          {safeFieldName}
          {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
        </label>
        <input
          type="text"
          id={inputId}
          className={`np-input${invalidClass}`}
          value={toDisplayText(sanitizedValue)}
          placeholder={safePlaceholder}
          readOnly
          required={required}
        />
        {errorMessage}
      </div>
    );
  }

  // Text input
  if (fieldType === 'text') {
    return (
      <div className="np-field">
        <label htmlFor={inputId}>
          {safeFieldName}
          {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
        </label>
        <input
          type="text"
          id={inputId}
          className={`np-input${invalidClass}`}
          value={toDisplayText(sanitizedValue)}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={safePlaceholder}
          readOnly={isReadonly}
          required={required}
        />
        {errorMessage}
      </div>
    );
  }

  // Number input
  if (fieldType === 'number') {
    return (
      <div className="np-field">
        <label htmlFor={inputId}>
          {safeFieldName}
          {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
        </label>
        <input
          type="number"
          id={inputId}
          className={`np-input${invalidClass}`}
          value={(sanitizedValue as number) || ''}
          onChange={(e) => handleChange(parseFloat(e.target.value))}
          placeholder={safePlaceholder}
          readOnly={isReadonly}
          required={required}
        />
        {errorMessage}
      </div>
    );
  }

  // Date input (flatpickr)
  if (fieldType === 'date') {
    return <FlatpickrDateField
      inputId={inputId}
      label={safeFieldName}
      required={required}
      value={toDisplayText(sanitizedValue)}
      readOnly={isReadonly}
      onChange={handleChange}
      error={error}
    />;
  }

  // Textarea
  if (fieldType === 'textarea') {
    return (
      <div className="np-field">
        <label htmlFor={inputId}>
          {safeFieldName}
          {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
        </label>
        <textarea
          id={inputId}
          className={`np-input${invalidClass}`}
          rows={4}
          value={toDisplayText(sanitizedValue)}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={safePlaceholder}
          readOnly={isReadonly}
          required={required}
          style={{ resize: 'vertical', minHeight: '100px' }}
        />
        {errorMessage}
      </div>
    );
  }

  // Rich text editor (Quill)
  if (fieldType === 'richtext') {
    return (
      <div className="np-field">
        <label>
          {safeFieldName}
          {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
        </label>
        {isReadonly ? (
          <div
            className="np-input"
            style={{ minHeight: '400px', backgroundColor: 'var(--np-surface-2)' }}
            dangerouslySetInnerHTML={{ __html: toDisplayText(sanitizedValue) }}
          />
        ) : (
          <div style={{ resize: 'vertical', overflow: 'auto', minHeight: '400px', maxHeight: '800px' }}>
            <ReactQuill
              value={toDisplayText(sanitizedValue)}
              onChange={(content) => handleChange(content)}
              modules={quillModules}
              placeholder={safePlaceholder}
              theme="snow"
              style={{ height: '350px' }}
            />
          </div>
        )}
        {errorMessage}
      </div>
    );
  }

  // Select dropdown (simple) — handles both 'select' and 'dropdown' catalogue types
  if ((fieldType === 'select' || fieldType === 'dropdown') && safeSelectOptions.length > 0) {
    return (
      <div className="np-field">
        <label htmlFor={inputId}>
          {safeFieldName}
          {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
        </label>
        <select
          id={inputId}
          className={`np-select${invalidClass}`}
          value={toDisplayText(sanitizedValue)}
          onChange={(e) => handleChange(e.target.value)}
          disabled={isReadonly}
          required={required}
        >
          <option value="">Select {safeFieldName}</option>
          {safeSelectOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        {errorMessage}
      </div>
    );
  }

  // Segmented pill toggle - a tactile alternative to a <select> for a small,
  // fixed set of options (each option is its own always-visible button rather
  // than hidden behind a dropdown).
  if (fieldType === 'segmented' && safeSelectOptions.length > 0) {
    const currentValue = toDisplayText(sanitizedValue);
    return (
      <div className="np-field">
        <label>
          {safeFieldName}
          {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
        </label>
        <div className={`np-pill-toggle-group${invalidClass}`} role="group">
          {safeSelectOptions.map((option) => {
            const isActive = option === currentValue;
            return (
              <button
                key={option}
                type="button"
                className={`np-pill-toggle np-tone-${segmentedPillTone(option)}${isActive ? ' active' : ''}`}
                disabled={isReadonly}
                onClick={() => handleChange(option)}
              >
                {option}
              </button>
            );
          })}
        </div>
        {errorMessage}
      </div>
    );
  }

  // Searchable dropdown (react-select with static options from catalogue)
  if (fieldType === 'searchable-dropdown') {
    const selectOptions = safeValueLabelOptions.length > 0
      ? safeValueLabelOptions
      : safeSelectOptions.map((o) => ({ value: o, label: o }));

    const selectedOption = selectOptions.find(
      (opt) => opt.value === toDisplayText(sanitizedValue)
    ) || null;

    return (
      <div className="np-field">
        <label>
          {safeFieldName}
          {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
        </label>
        <Select
          options={selectOptions}
          value={selectedOption}
          onChange={(option) => handleChange((option as any)?.value || '')}
          placeholder={safePlaceholder || `Select ${safeFieldName}`}
          isDisabled={isReadonly}
          isClearable
          isSearchable
          menuPortalTarget={document.body}
          styles={reactSelectStyles(isReadonly, !!error)}
        />
        {errorMessage}
      </div>
    );
  }

  // Multi-select (react-select, multiple values stored as array)
  if (fieldType === 'multiselect' || fieldType === 'multi-select') {
    const selectOptions = safeValueLabelOptions.length > 0
      ? safeValueLabelOptions
      : safeSelectOptions.map((o) => ({ value: o, label: o }));

    const normalizedValue: string[] = Array.isArray(sanitizedValue)
      ? (sanitizedValue as string[])
      : typeof sanitizedValue === 'string' && sanitizedValue
        ? sanitizedValue.split(',').map((s) => s.trim()).filter(Boolean)
        : [];

    const selectedOptions = selectOptions.filter((opt) => normalizedValue.includes(opt.value));

    return (
      <div className="np-field">
        <label>
          {safeFieldName}
          {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
        </label>
        <Select
          options={selectOptions}
          value={selectedOptions}
          onChange={(opts) => handleChange((opts as any[]).map((o) => o.value))}
          placeholder={safePlaceholder || `Select ${safeFieldName}`}
          isDisabled={isReadonly}
          isClearable
          isSearchable
          isMulti
          menuPortalTarget={document.body}
          styles={reactSelectStyles(isReadonly, !!error)}
        />
        {errorMessage}
      </div>
    );
  }

  // Procedure select (document-driven dropdown)
  if (fieldType === 'procedure') {
    const selectedOption = safeValueLabelOptions.find((opt) => opt.value === toDisplayText(sanitizedValue)) || null;

    return (
      <div className="np-field">
        <label>
          {safeFieldName}
          {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
        </label>
        {isReadonly && selectedOption ? (
          <a
            href={`/documents/${encodeURIComponent(selectedOption.value)}`}
            className="np-input d-flex align-items-center justify-content-between"
            style={{ cursor: 'pointer', textDecoration: 'none', background: 'var(--np-surface-2)' }}
            title="Click to view document"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>{selectedOption.label}</span>
            <i className="ri ri-external-link-line" style={{ color: 'var(--bs-secondary-color)' }}></i>
          </a>
        ) : (
          <>
            <Select
              options={safeValueLabelOptions}
              value={selectedOption}
              onChange={(option) => handleChange(option?.value || '')}
              placeholder={safePlaceholder || `Select ${safeFieldName}`}
              isDisabled={isReadonly}
              isClearable
              isSearchable
              menuPortalTarget={document.body}
              styles={reactSelectStyles(isReadonly, !!error)}
            />
            {safeValueLabelOptions.length === 0 && (
              <small className="np-hint"><i className="ri-list-check-2 me-1"></i>No procedures available</small>
            )}
          </>
        )}
        {errorMessage}
      </div>
    );
  }

  // Person select (searchable dropdown)
  if (fieldType === 'person') {
    const personOptions = people.map((person) => ({
      value: person.personId,
      label: person.personName,
    }));

    const selectedOption = personOptions.find(
      (opt) => opt.value === sanitizedValue
    ) || null;

    return (
      <div className="np-field">
        <label>
          {safeFieldName}
          {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
        </label>
        <Select
          options={personOptions}
          value={selectedOption}
          onChange={(option) => handleChange(option?.value || '')}
          placeholder={safePlaceholder || `Select ${safeFieldName}`}
          isDisabled={isReadonly}
          isClearable
          isSearchable
          menuPortalTarget={document.body}
          styles={reactSelectStyles(isReadonly, !!error)}
        />
        {errorMessage}
      </div>
    );
  }

  // Asset select (searchable dropdown) - supports both single and multi-select
  if (fieldType === 'asset') {
    const assetOptions = assets.map((asset) => ({
      value: asset.assetId,
      label: asset.name,
    }));

    // Default to multi-select for common asset list fields.
    const isMulti = Array.isArray(sanitizedValue) || fieldId === 'affected_assets';

    const normalizedMultiValue: string[] = Array.isArray(sanitizedValue)
      ? (sanitizedValue as string[])
      : typeof sanitizedValue === 'string' && sanitizedValue
        ? sanitizedValue.split(',').map((s) => s.trim()).filter(Boolean)
        : [];
    
    const selectedOption = isMulti
      ? assetOptions.filter((opt) => normalizedMultiValue.includes(opt.value))
      : assetOptions.find((opt) => opt.value === sanitizedValue) || null;

    return (
      <div className="np-field">
        <label>
          {safeFieldName}
          {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
        </label>
        <Select
          options={assetOptions}
          value={selectedOption}
          onChange={(option) => {
            if (isMulti) {
              const selected = option as Array<{ value: string; label: string }>;
              handleChange(selected ? selected.map(o => o.value) : []);
            } else {
              const selected = option as { value: string; label: string } | null;
              handleChange(selected?.value || '');
            }
          }}
          placeholder={safePlaceholder || `Select ${safeFieldName}`}
          isDisabled={isReadonly}
          isClearable
          isSearchable
          isMulti={isMulti}
          menuPortalTarget={document.body}
          styles={reactSelectStyles(isReadonly, !!error)}
        />
        {errorMessage}
      </div>
    );
  }

  // Department select (searchable dropdown)
  if (fieldType === 'department') {
    const departmentOptions = departments.map((dept) => ({
      value: dept.departmentId,
      label: dept.name,
    }));

    const selectedOption = departmentOptions.find(
      (opt) => opt.value === sanitizedValue
    ) || null;

    return (
      <div className="np-field">
        <label>
          {safeFieldName}
          {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
        </label>
        <Select
          options={departmentOptions}
          value={selectedOption}
          onChange={(option) => handleChange(option?.value || '')}
          placeholder={safePlaceholder || `Select ${safeFieldName}`}
          isDisabled={isReadonly}
          isClearable
          isSearchable
          menuPortalTarget={document.body}
          styles={reactSelectStyles(isReadonly, !!error)}
        />
        {errorMessage}
      </div>
    );
  }

  // Risk select (searchable dropdown)
  if (fieldType === 'risk') {
    const riskOptions = risks.map((risk) => {
      const titleField = risk.fields?.risk_title;
      // Sanitize in case risk_title is stored as {value, label} object
      const title = titleField && typeof titleField === 'object' && 'value' in titleField
        ? titleField.value
        : titleField;
      return{
        value: risk.riskId,
        label: title || risk.riskId,
      };
    });

    const selectedOption = riskOptions.find(
      (opt) => opt.value === sanitizedValue
    ) || null;

    return (
      <div className="np-field">
        <label>
          {safeFieldName}
          {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
        </label>
        <Select
          options={riskOptions}
          value={selectedOption}
          onChange={(option) => handleChange(option?.value || '')}
          placeholder={safePlaceholder || `Select ${safeFieldName}`}
          isDisabled={isReadonly}
          isClearable
          isSearchable
          menuPortalTarget={document.body}
          styles={reactSelectStyles(isReadonly, !!error)}
        />
        {errorMessage}
      </div>
    );
  }

  // Calculated field (readonly) - looks like a field, background tinted by RAG rating
  if (fieldType === 'calculated') {
    const displayVal = toDisplayText(sanitizedValue);
    const valueStr = displayVal.toLowerCase();

    let tone: 'success' | 'warning' | 'danger' | null = null;

    if (valueStr.includes('critical') || valueStr.includes('high')) {
      tone = 'danger';
    } else if (valueStr.includes('medium') || valueStr.includes('moderate')) {
      tone = 'warning';
    } else if (valueStr.includes('low') || valueStr.includes('minimal')) {
      tone = 'success';
    } else if (valueStr.endsWith('%')) {
      const num = parseInt(valueStr);
      tone = num >= 71 ? 'success' : num >= 41 ? 'warning' : 'danger';
    }

    return (
      <div className="np-field">
        <label>
          {safeFieldName}
          <i className="ri-calculator-line" style={{ fontSize: '0.75rem', marginLeft: 4, color: 'var(--bs-secondary-color)' }} title="Auto-calculated"></i>
        </label>
        <div
          className="np-input d-flex align-items-center justify-content-center text-center"
          style={{
            backgroundColor: tone ? `var(--bs-${tone}-bg-subtle)` : 'transparent',
            color: tone ? `var(--bs-${tone}-text-emphasis)` : 'inherit',
            borderColor: tone ? `var(--bs-${tone}-border-subtle)` : 'var(--np-border-strong)',
            fontWeight: displayVal ? 600 : 400,
            cursor: 'default',
            userSelect: 'none',
          }}
        >
          {displayVal || <span style={{ color: 'var(--bs-secondary-color)', fontWeight: 400, fontStyle: 'italic' }}>Not calculated yet</span>}
        </div>
      </div>
    );
  }

  // Default fallback (text input)
  return (
    <div className="np-field">
      <label htmlFor={inputId}>
        {safeFieldName}
        {required && <span style={{ color: 'var(--bs-danger)', marginLeft: 4 }}>*</span>}
      </label>
      <input
        type="text"
        id={inputId}
        className={`np-input${invalidClass}`}
        value={toDisplayText(sanitizedValue)}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={safePlaceholder}
        readOnly={isReadonly}
        required={required}
      />
      {errorMessage}
      <small className="np-hint">Unsupported field type: {safeFieldTypeText}</small>
    </div>
  );
};
